import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import LanguageIcon from '@mui/icons-material/Language';
import XIcon from '@mui/icons-material/X';
import CodeIcon from '@mui/icons-material/Code';
import ImageIcon from '@mui/icons-material/Image';
import LaptopIcon from '@mui/icons-material/Laptop';
import GetAppIcon from '@mui/icons-material/GetApp';
import FadeInSection from './FadeInSection';
import '../styles/Projects.css';

const projectData = [
  {
    title: "Midnight Showroom",
    description: [
      "A night-time 3D car showroom in the browser. Cars fly together part by part, drive in and out when you switch, and come with sourced specs and real factory colours.",
    ],
    tech: ["JavaScript", "Three.js", "anime.js", "WebGL", "Parcel"],
    github: "https://github.com/OkeahDavid/midnight-showroom",
    link: "https://midnight-showroom.netlify.app/",
    image: "/project-images/midnight-showroom.png",
    category: "graphics",
  },
  {
    title: "pixelbench",
    description: [
      "Speedtest for your CPU vs GPU — an image-processing benchmark with a native OpenCV CLI (published on PyPI) and an in-browser WebGPU version.",
    ],
    tech: ["Python", "OpenCV", "React", "TypeScript", "WebGPU", "Vite"],
    github: "https://github.com/OkeahDavid/pixelbench",
    link: "https://pixelbench.netlify.app/",
    image: "/project-images/pixelbench.png",
    category: "graphics",
  },
  {
    title: "Metric Hub",
    description: [
      "A privacy-focused analytics platform for tracking website statistics across multiple projects.",
    ],
    tech: ["Next.js", "TypeScript", "Tailwind CSS"],
    github: "https://github.com/OkeahDavid/Metrics-hub",
    link: "https://metrics-hub.netlify.app/",
    image: "/project-images/metrics-hub.png", // Default image that will be used if available
    category: "web",
  },
  {
    title: "AfterThought",
    description: [
      "A privacy-focused journaling app for capturing daily thoughts and rediscovering them randomly each day.",
      "Includes a companion landing page website built with modern web technologies."
    ],
    tech: ["TypeScript", "CSS", "HTML"],
    github: "https://github.com/OkeahDavid/AfterThought-Web",
    link: "https://play.google.com/store/apps/details?id=com.afterthought.app",
    image: "/project-images/afterthought.png",
    category: "web",
  },
  {
    title: "Earth",
    description: [
      "An interactive 3D simulation of Earth and its moon with zoom and rotation controls.",
    ],
    tech: ["Node.js", "Three.js", "HTML", "globe.gl"],
    github: "https://github.com/OkeahDavid/Earth",
    link: "https://interactive-earth-3d.netlify.app/",
    preview: true,
    image: "/project-images/earth2.png",
    category: "graphics",
  },
  {
    title: "Flowgen",
    description: [
      "A drag-and-drop interface for building AI agent workflows using Microsoft's AutoGen framework.",
    ],
    tech: ["React", "TypeScript", "Python", "FastAPI", "Material UI", "AutoGen"],
    github: "https://github.com/OkeahDavid/Flowgen",
    link: "https://flowgen-ai.netlify.app/",
    image: "/project-images/flowgen.png",
    category: "ai",
  },
  {
    title: "His Family Global Outreach Website",
    description: [
      "A responsive website with interactive elements and modern design for a global outreach organization.",
    ],
    tech: ["HTML", "CSS", "TypeScript", "React"],
    github: "https://github.com/OkeahDavid/HFGO",
    link: "https://hfgo.netlify.app",
    image: "/project-images/hfgo.png",
    category: "web",
  },
  {
    title: "VLR Stats MCP",
    description: [
      "A Model Context Protocol server providing Claude AI access to VALORANT esports data from VLR.gg.",
    ],
    tech: ["Python", "FastMCP", "BeautifulSoup4", "httpx"],
    github: "https://github.com/OkeahDavid/vlr-stats-mcp",
    image: "/project-images/vlr-stats.png",
    category: "ai",
  },
  {
    title: "Live Diaries",
    description: [
      "A modern landing page for the Live Diaries mobile app.",
    ],
    tech: ["Next.js", "TypeScript", "Tailwind CSS"],
    github: "https://github.com/OkeahDavid/LiveDiaries",
    link: "https://livediaries.netlify.app/",
    image: "/project-images/live-diaries.png",
    category: "web",
  },
  {
    title: "CIFAR-10 CNN Classifier",
    description: [
      "A convolutional neural network trained to classify images from the CIFAR-10 dataset.",
    ],
    tech: ["Python", "Keras", "TensorFlow"],
    github: "https://github.com/OkeahDavid/CIFAR-10-CNN-Classifier",
    image: "/project-images/cifar10.png",
    category: "ai",
  },
  
  
  // More projects...
];

// Define categories with their display names and icons
const categories = [
  { id: "all", label: "All Projects", icon: <LaptopIcon /> },
  { id: "web", label: "Web Development", icon: <LanguageIcon /> },
  { id: "ai", label: "AI & Machine Learning", icon: <CodeIcon /> },
  { id: "graphics", label: "Graphics", icon: <ImageIcon /> },
];

const Projects = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [imagesLoading, setImagesLoading] = useState({});

  const filteredProjects = activeCategory === "all" 
    ? projectData 
    : projectData.filter(project => project.category === activeCategory);

  // Track image loading status
  const handleImageLoad = (index) => {
    setImagesLoading(prev => ({...prev, [index]: false}));
  };

  useEffect(() => {
    // Initialize loading state for all projects
    const loadingStates = {};
    projectData.forEach((_, index) => {
      loadingStates[index] = true;
    });
    setImagesLoading(loadingStates);

    // Preload images
    projectData.forEach((project, index) => {
      if (project.image) {
        const img = new Image();
        img.src = project.image;
        img.onload = () => handleImageLoad(index);
        img.onerror = () => handleImageLoad(index);
      } else {
        handleImageLoad(index);
      }
    });
  }, []);

  return (
    <Box id="projects" className="projects-container">
      <FadeInSection>
        <Typography variant="h4" className="section-header">
          / projects
        </Typography>
      </FadeInSection>
      
      <FadeInSection delay={200}>
        <Box className="project-filters">
          {categories.map((category) => (
            <Chip
              key={category.id}
              icon={category.icon}
              label={category.label}
              onClick={() => setActiveCategory(category.id)}
              className={`category-chip ${activeCategory === category.id ? 'active' : ''}`}
            />
          ))}
        </Box>
      </FadeInSection>

      <Box className="projects-grid">
        {filteredProjects.map((project, index) => (
          <FadeInSection key={index} delay={100 * (index % 5)}>
            <Box 
              className={`project-card ${hoveredCard === index ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {project.image && (
                <Box 
                  className={`project-thumbnail ${imagesLoading[index] ? 'loading' : ''}`} 
                  style={{ 
                    backgroundImage: imagesLoading[index] ? 'none' : `url(${project.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {imagesLoading[index] && (
                    <Box className="image-loading-placeholder" />
                  )}
                  {(!project.image || imagesLoading[index]) && (
                    <LaptopIcon className="fallback-icon" />
                  )}
                </Box>
              )}
              
              <Box className="project-header">
                <Typography variant="h6" className="project-title">{project.title}</Typography>
              </Box>
              
              {project.description && project.description.map((point, idx) => (
                <Typography key={idx} variant="body2" className="project-description">{point}</Typography>
              ))}
              
              <Box className="tech-stacks">
                {project.tech && project.tech.map((tech, idx) => (
                  <Box key={idx} className="tech-chip">{tech}</Box>
                ))}
              </Box>
              
              <Box className="project-actions">
                {project.github && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    href={project.github} 
                    target="_blank" 
                    rel="noopener" 
                    startIcon={<GitHubIcon />}
                    className="action-button"
                    aria-label="View code"
                  >
                    Code
                  </Button>
                )}
                {project.link && (
                  <Button 
                    variant="contained" 
                    size="small"
                    href={project.link} 
                    target="_blank" 
                    rel="noopener" 
                    startIcon={project.link.includes('twitter.com') ? <XIcon /> : project.link.includes('play.google.com') ? <GetAppIcon /> : <LanguageIcon />}
                    className="action-button"
                    aria-label="View live site"
                  >
                    {project.link.includes('twitter.com') ? 'Twitter' : project.link.includes('play.google.com') ? 'Download' : 'Live'}
                  </Button>
                )}
              </Box>
            </Box>
          </FadeInSection>
        ))}
      </Box>
    </Box>
  );
};

export default Projects;
